"use client";

import { Box, Button, Flex, Text } from "@chakra-ui/react";
import {
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from "react";
import { CONSENT_CHANGED_EVENT, getConsent } from "@/lib/analytics";
import {
  releaseBodyPaddingBottom,
  setBodyPaddingBottom,
} from "@/lib/layout/body-padding-bottom";

/**
 * The one way to buy, kept in reach.
 *
 * The considered ask lives at the foot of the manifest, where it belongs — a
 * reader should meet the price after seeing what is withheld, not before. But
 * measured on Rome that ask sat at y=3798 of a 5290px page: **72% down, 4.2
 * screens**, and it was the only place the price appeared at all. A reader
 * convinced by the market tiles at 900px had nothing to press for three more
 * screens, which is the paywall failure mode of making people hunt for pricing.
 *
 * So the ask follows them instead of being duplicated:
 *
 *  - **Armed, not immediate.** It appears only once `armRef` has been passed —
 *    the top of the locked manifest — so it never precedes the value that earns
 *    it, and never covers the free proof above.
 *  - **Never a second CTA.** It hides while `askRef` is on screen. `design.md`
 *    allows one filled CTA per viewport, and the whole point of the last gate
 *    rewrite was killing seven of them; this is the same single ask, relocated,
 *    never an additional one.
 *  - **Dismissible.** A commercial bar the reader cannot get rid of is the
 *    nagging this page was already audited for once.
 */
export function StickyPurchaseBar({
  destinationName,
  price,
  armRef,
  askRef,
  onUnlock,
  isSubmitting,
  error,
}: {
  destinationName: string;
  price: string;
  /** Passed by the reader before the bar may appear. */
  armRef: RefObject<HTMLDivElement | null>;
  /** The real ask. While this is visible the bar stands down. */
  askRef: RefObject<HTMLDivElement | null>;
  onUnlock: () => void;
  isSubmitting: boolean;
  error: string | null;
}): ReactElement | null {
  const [isConsentResolved, setConsentResolved] = useState(false);
  const [isArmed, setArmed] = useState(false);
  const [isAskVisible, setAskVisible] = useState(false);
  const [isDismissed, setDismissed] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  // The bar shares the bottom edge with the cookie consent banner (rendered
  // globally from providers), which paints above it at a higher z-index — so
  // while consent is unresolved the bar is not merely overlapped, it is fully
  // occluded and its price and Unlock button are unreachable. Rather than stack
  // two fixed bars, the bar stands down until a consent choice exists and takes
  // the edge as the single occupant once the banner leaves.
  //
  // Read in an effect, never during render: reading localStorage at render time
  // would make the server markup (consent always unknown) disagree with the
  // client. Hidden-until-known is the bar's natural initial state — it already
  // starts unarmed — so there is no flash. The banner dispatches
  // CONSENT_CHANGED_EVENT the moment a choice is made, letting the bar arm on
  // the same page without a reload.
  useEffect(() => {
    if (getConsent() !== null) {
      setConsentResolved(true);
      return;
    }
    const onChange = () => setConsentResolved(true);
    window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
  }, []);

  // Two observers rather than scroll maths: the page's section heights are
  // data-dependent (a city with fewer movers is a shorter page), so no pixel
  // threshold would mean the same thing twice.
  //
  // `.current` is read inside the effect, never during render: refs are only
  // populated at commit and mutating one does not re-render, so a render-time
  // read would capture `null` forever and the observers would never attach.
  useEffect(() => {
    const el = armRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        // Armed as soon as the free/paid boundary reaches the viewport — the
        // reader has the proof above and is now looking at what is withheld.
        // Waiting for it to leave upward cost a whole screen of manifest with
        // nothing to press, which is the burial this bar exists to undo.
        if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
          setArmed(true);
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [armRef]);

  useEffect(() => {
    const el = askRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => setAskVisible(entries[0]?.isIntersecting ?? false),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [askRef]);

  const isVisible =
    isConsentResolved && isArmed && !isAskVisible && !isDismissed;

  // A fixed bar occupies no space in flow, so without this it sits on top of
  // whatever is last on the page — measured at the foot of Rome, it covered the
  // footer's "Privacy inquiries" and copyright rows. Measured rather than
  // hardcoded: the bar wraps to two lines on a narrow screen.
  //
  // Recomputed, not read once: the bar's height is width-dependent (69px on one
  // line, 98px wrapped at ~320px) and can change without `isVisible` changing —
  // a phone rotated landscape→portrait keeps the bar shown but re-wraps it
  // taller, and a padding frozen at the old height would let the taller bar
  // cover the footer again. A ResizeObserver on the bar is the single trigger:
  // every viewport change that matters re-wraps the bar, and one that leaves the
  // bar's height alone needs no update anyway.
  useEffect(() => {
    if (!isVisible) return;
    const el = barRef.current;
    if (!el) return;

    const sync = () => {
      const current = barRef.current;
      if (current) {
        setBodyPaddingBottom("sticky-purchase-bar", current.offsetHeight);
      }
    };
    sync();

    // The write is deferred to the next frame, not run inside the observer
    // callback, because it is not inert: growing `body` padding-bottom can add a
    // vertical scrollbar, which narrows the viewport, which can re-wrap the bar
    // taller and fire the observer again — a feedback path that surfaces as
    // "ResizeObserver loop completed with undelivered notifications". Writing on
    // the next frame breaks that loop.
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    };

    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      releaseBodyPaddingBottom("sticky-purchase-bar");
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      ref={barRef}
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={20}
      /* Opaque, and lifted off the page.

         This shipped as `--color-background` at 82% with a 12px blur, copying
         the header's language, and was invisible: 82% white over a white page
         is white, and the blur had nothing to soften. Tinting the surface did
         not rescue it either — `--color-surface-tint` is 1.04:1 on white.

         The header gets away with a hairline because the top of the page has an
         edge. The bottom of a scrolling document has none, so this needs real
         elevation: `--shadow-raised` is the one upward shadow in the system,
         added for exactly this. */
      bg="var(--color-background)"
      borderTop="1px solid var(--color-border-strong)"
      boxShadow="var(--shadow-raised)"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <Flex
        maxW="1040px"
        mx="auto"
        px={{ base: 6, md: 8 }}
        py={{ base: 3, md: 0 }}
        minH={{ base: "auto", md: "68px" }}
        gap={{ base: 3, md: 6 }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box minW={0}>
          {/* The label is desktop-only: on a 320px screen the price and the
              button are the whole message, and truncating a city name to make
              room for a heading nobody reads is worse than dropping it. */}
          <Text
            display={{ base: "none", md: "block" }}
            fontSize="15px"
            color="var(--color-text-primary)"
            lineHeight="1.3"
            truncate
          >
            The full {destinationName} report
          </Text>
          <Text
            fontFamily="var(--font-geist-mono)"
            /* Was 12px tertiary — the smallest, faintest thing on a bar whose
               entire job is naming a price. design.md § CTA voice puts the
               price next to the button that charges it; that only means
               anything if it can be read. */
            fontSize="13px"
            color={error ? "rgb(220, 38, 38)" : "var(--color-text-secondary)"}
            style={{ fontVariantNumeric: "tabular-nums" }}
            lineHeight="1.4"
          >
            {error ?? `${price} a month · cancel any time`}
          </Text>
        </Box>

        <Flex gap={2} alignItems="center" flexShrink={0}>
          <Button
            onClick={onUnlock}
            loading={isSubmitting}
            disabled={isSubmitting}
            bg="var(--color-primary)"
            color="var(--color-on-primary)"
            fontWeight="600"
            fontSize={{ base: "14px", md: "15px" }}
            h="44px"
            px={{ base: 5, md: 6 }}
            borderRadius="var(--radius-sm)"
            whiteSpace="nowrap"
            transition="var(--transition-fast)"
            _hover={{ bg: "var(--color-primary-hover)" }}
            _active={{ bg: "var(--color-primary-strong)" }}
          >
            Unlock {destinationName}
          </Button>

          {/* Secondary voice, per design.md: a bare glyph, never a second fill. */}
          <Button
            onClick={() => setDismissed(true)}
            aria-label="Hide the purchase bar"
            bg="transparent"
            color="var(--color-text-tertiary)"
            fontSize="18px"
            h="44px"
            w="36px"
            minW="36px"
            borderRadius="var(--radius-sm)"
            transition="var(--transition-fast)"
            _hover={{ color: "var(--color-text-primary)" }}
          >
            ×
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}
